import {
  Component,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  DestroyRef,
  Input,
  ChangeDetectorRef,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  DeleteDocumentDialogComponent,
  DeleteDocumentDialogData,
} from 'src/app/components/data-displayer/document-table/delete-document-dialog/delete-document-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { getDocumentMask, validateBrazilianDocument } from 'src/app/helpers/documents-utils';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  from,
  Observable,
  skip,
  switchMap,
} from 'rxjs';

import { TrackingRecordRegisterDialogComponent } from 'src/app/components/entity-manager/tracking-record-register-dialog.ts/tracking-record-register-dialog.component';
import { HttpStatus } from 'src/app/shared/enums/http-status.enum';
import { LocalStorageService } from 'src/app/services/storage/local-storage.service';
import { WalletResponseDto } from 'src/generated-sources/openapi/wallet-service/wallet';
import { DocumentIndicatorsModalComponent } from 'src/app/components/list-individual/document-indicators-modal/document-indicators-modal.component';
import { DocumentWalletsModalComponent } from 'src/app/components/list-individual/document-wallets-modal/document-wallets-modal.component';
import { FilterTagsComponent } from './filter-tags/filter-tags.component';
import { NO_NAME } from 'src/app/components/constants';
import { AdvancedFiltersComponent } from './advanced-filters/advanced-filters.component';
import {
  TrackingRegistryActionsComponent,
  TrackingRegistryAction,
} from './components/tracking-registry-actions/tracking-registry-actions.component';
import { ButtonComponent } from 'src/app/components/button/button.component';
import { SearchInputComponent } from 'src/app/shared/components/search-input/search-input.component';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from 'src/app/shared/shared.module';
import { MatOptionModule } from '@angular/material/core';
import { WalletDataService } from 'src/app/services/data/wallet-data.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ListFilterService } from './services/list-filter.service';
import { ListIndividualFacade, TrackingRegistryView } from './services/list-individual.facade';
import { FileUtilsService } from 'src/app/services/file-utils.service';
import { ListIndividualStore } from './services/list-individual.store';
import type { ListIndividualState } from './services/list-individual.store';
import { WalletSelectComponent } from 'src/app/shared/components/wallet-select/wallet-select.component';
import {
  ConfirmDialogResult,
  ConfirmDialogResultType,
} from 'src/app/shared/components/modals/confirmation-dialog/confirmation-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { GenericTableComponent } from 'src/app/shared/components/generic-table';
import { TableCellDirective } from 'src/app/shared/components/generic-table/directives/table-cell.directive';
import {
  getListIndividualTableConfig,
  LIST_INDIVIDUAL_NO_DATA_MESSAGE,
} from './list-individual-table-config';

@Component({
  selector: 'app-list-individual',
  templateUrl: './list-individual.component.html',
  styleUrls: ['./list-individual.component.scss'],
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    FormsModule,
    SearchInputComponent,
    GenericTableComponent,
    TableCellDirective,
    ButtonComponent,
    FilterTagsComponent,
    AdvancedFiltersComponent,
    WalletSelectComponent,
    TrackingRegistryActionsComponent,
  ],
  providers: [ListFilterService, ListIndividualFacade, ListIndividualStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListIndividualComponent implements OnInit, OnDestroy {
  @Output()
  newDocumentShortcutClick = new EventEmitter();

  @Output()
  massiveLoadShortcutClick = new EventEmitter();

  @Output()
  attributeEvolutionClick = new EventEmitter<{
    planId: string;
    documentNumber: string;
    registryId: string;
    inclusionDate: Date;
  }>();

  @Input()
  showAdminActions: boolean = false;

  tableConfig = getListIndividualTableConfig();
  noDataMessage: string = LIST_INDIVIDUAL_NO_DATA_MESSAGE;

  readonly showFilterFields$ = new BehaviorSubject<boolean>(false);

  personType = this.listIndividualFacade.getPersonTypeCurrentValue();

  state$?: Observable<ListIndividualState>;
  selectedWallet: WalletResponseDto | null = null;

  readonly pageSize = 10;

  readonly isAlert30DaysWallet = toSignal(this.store.selectIsAlert30DaysWallet$(), {
    initialValue: false,
  });

  constructor(
    private readonly dialog: MatDialog,
    private readonly snackBarService: SnackbarService,
    protected readonly filterService: ListFilterService,
    private readonly listIndividualFacade: ListIndividualFacade,
    private readonly localStorageService: LocalStorageService,
    private readonly destroyRef: DestroyRef,
    private readonly fileUtilsService: FileUtilsService,
    private readonly store: ListIndividualStore,
    private readonly walletDataService: WalletDataService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.state$ = this.store.getState$();

    this.listIndividualFacade
      .getPersonType()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((personType) => {
        this.personType = personType;
        this.store.setLoading(true);
        this.cdr.markForCheck();
      });

    this.filterService.filterValues$
      .pipe(takeUntilDestroyed(this.destroyRef), skip(1))
      .subscribe((filters) => {
        if (filters) {
          this.store.setFilters(filters);
          this.loadData();
        }
      });

    this.listIndividualFacade
      .getDocumentAdded$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadData();
      });
  }

  private loadData(): void {
    const state = this.store.getStateValue();

    this.store.setLoading(true);

    const apiParams = this.listIndividualFacade.buildSearchParams(
      state.filters,
      state.page,
      state.selectedWalletId,
      state.pageSize,
    );

    this.listIndividualFacade
      .getTrackingRegistries(apiParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.store.setItems(response.items, response.total);
          this.store.setCanDownloadReport(
            this.listIndividualFacade.canDownloadReport(response.total),
          );
        },
        error: () => {
          this.snackBarService.error(
            $localize`:@@js_getDocumentsListError:Error al intentar obtener lista de documentos`,
          );
          this.store.setItems([], 0);
        },
      });
  }

  get documentInputSearchLabel() {
    return $localize`:@@search_document:Buscar documento`;
  }

  get documentSearchMask(): string {
    return getDocumentMask(this.personType);
  }

  get documentValidator() {
    return validateBrazilianDocument;
  }

  get urlAssets() {
    return this.localStorageService.getItem('urlAssets');
  }

  onWalletChange(walletId: string) {
    this.store.setSelectedWallet(walletId);
    this.store.setAlert30DaysWallet(this.listIndividualFacade.resolveIsAlert30DaysWallet(walletId));
    this.loadData();
  }

  onSearchTriggered() {
    this.filterService.applyASingleFilter('document');
  }

  changePage(page: number = 0) {
    this.store.setPage(page);
    this.loadData();
  }

  handleShowWallets(trackingRegistry: TrackingRegistryView) {
    this.dialog.open(DocumentWalletsModalComponent, {
      data: trackingRegistry,
      width: '650px',
    });
  }

  handleEditTrackingRegistry(registry: TrackingRegistryView) {
    const state = this.store.getStateValue();

    this.listIndividualFacade
      .findByIdForEdit(registry.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((dialogData) => {
          const dialogRef = this.dialog.open(TrackingRecordRegisterDialogComponent, {
            data: { ...dialogData, walletId: state.selectedWalletId },
            disableClose: true,
          });
          return dialogRef.afterClosed();
        }),
        filter((tracking: ConfirmDialogResultType) => tracking === ConfirmDialogResult.Confirm),
      )
      .subscribe(() => {
        this.loadData();
      });
  }

  handleShowIndicators(trackingRegistry: TrackingRegistryView) {
    if (!trackingRegistry.restrictives.length) return;

    this.dialog.open(DocumentIndicatorsModalComponent, {
      data: trackingRegistry,
      width: '650px',
    });
  }

  handleDeleteTrackingRegistry(trackingRegistry: TrackingRegistryView) {
    const dialogData: DeleteDocumentDialogData = {
      ...trackingRegistry,
      isAlert30DaysWallet: this.isAlert30DaysWallet(),
    };

    const deleteTrackingDialogRef = this.dialog.open(DeleteDocumentDialogComponent, {
      data: dialogData,
    });

    deleteTrackingDialogRef
      .afterClosed()
      .pipe(
        filter((result: ConfirmDialogResultType) => result === ConfirmDialogResult.Confirm),
        switchMap(() => {
          const { selectedWalletId, isAlert30DaysWallet } = this.store.getStateValue();
          const walletId = isAlert30DaysWallet ? selectedWalletId : undefined;
          return this.listIndividualFacade.deleteRegistry(trackingRegistry.id, walletId);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          if (response?.code === HttpStatus.OK && !response?.successful) {
            this.snackBarService.error(`${response.message}`);
          } else {
            this.snackBarService.normal($localize`:@@successfullyDeleted:Eliminado con éxito.`);
            this.store.removeItem(trackingRegistry.id);
          }
        },
        error: ({ error }) => {
          this.snackBarService.error(error?.message);
        },
      });
  }

  handleAttributeEvolution(trackingRegistry: TrackingRegistryView) {
    const { selectedWalletId } = this.store.getStateValue();
    const selectedWallet = this.walletDataService
      .getStateValue()
      ?.wallets?.find((w) => w.id === selectedWalletId);
    const planId = selectedWallet?.planId;

    if (!planId) {
      this.snackBarService.error(
        $localize`:@@noPlanAvailable:No hay plan disponible para este documento.`,
      );
      return;
    }

    const inclusionDate = trackingRegistry.inclusionDate;

    this.attributeEvolutionClick.emit({
      planId: planId,
      documentNumber: trackingRegistry.document,
      registryId: trackingRegistry.registryId,
      inclusionDate,
    });
  }

  toggleFavorite(trackingRegistry: TrackingRegistryView) {
    this.listIndividualFacade
      .toggleFavorite(trackingRegistry.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.store.updateItem(trackingRegistry.id, {
            isFavorite: !trackingRegistry.isFavorite,
          });
        },
        error: () => {
          const message = trackingRegistry.isFavorite
            ? $localize`:@@errorUnmarkFavorite:Error al intentar desmarcar como favorito.`
            : $localize`:@@errorMarkFavorite:Error al intentar marcar como favorito.`;

          this.snackBarService.error(message);
        },
      });
  }

  onTrackingRegistryAction(event: {
    action: TrackingRegistryAction;
    item: TrackingRegistryView;
  }): void {
    switch (event.action) {
      case 'edit':
        this.handleEditTrackingRegistry(event.item);
        break;
      case 'delete':
        this.handleDeleteTrackingRegistry(event.item);
        break;
      case 'toggleFavorite':
        this.toggleFavorite(event.item);
        break;
      case 'attributesEvolution':
        this.handleAttributeEvolution(event.item);
        break;
    }
  }

  handleNewDocumentShortcutClick() {
    this.newDocumentShortcutClick.emit();
  }

  handleMassiveLoadShortcutClick() {
    this.massiveLoadShortcutClick.emit();
  }

  getPersonName(trackingRegistry: TrackingRegistryView) {
    return trackingRegistry.name || NO_NAME;
  }

  toggleFilterFields(): void {
    this.showFilterFields$.next(!this.showFilterFields$.value);
  }

  reportPerson(totalRegistries: number) {
    if (!this.listIndividualFacade.canDownloadReport(totalRegistries)) {
      this.snackBarService.normal(
        $localize`:@@maxQuantityToExportDocumentsReport:La exportación está limitada a 1000 registros. Utilice filtros más específicos para refinar su búsqueda.`,
      );
      return;
    }

    const state = this.store.getStateValue();
    const filters = this.filterService.getForm().value;

    const searchParams = this.listIndividualFacade.buildSearchParams(
      filters,
      0,
      state.selectedWalletId,
    );

    this.snackBarService.normal($localize`:@@downloadHasStarted:Su descarga ha iniciado...`);

    this.listIndividualFacade
      .downloadReport(searchParams)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error: HttpErrorResponse) => {
          return from(this.handleDownloadError(error)).pipe(switchMap(() => EMPTY));
        }),
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.fileUtilsService.downloadBlobFile(response, 'relatorio-de-documentos');
          }
        },
      });
  }

  private async handleDownloadError(error: HttpErrorResponse): Promise<void> {
    let message = $localize`:@@errorDownloadingFile:Error ao baixar o arquivo.`;

    if (error.error instanceof Blob) {
      try {
        const errorText = await error.error.text();
        if (errorText) {
          const errorJson = JSON.parse(errorText);
          message = errorJson.message || message;
        }
      } catch {}
    } else if (error?.error?.message) {
      message = error.error.message;
    } else if (error.message) {
      message = error.message;
    }

    this.snackBarService.normal(message, { duration: 10000 });
  }

  ngOnDestroy(): void {
    this.showFilterFields$.complete();
  }
}
